import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Box, BoxProps, MantineTheme, rem, ScrollArea } from '@mantine/core'
import { useIntl } from 'react-intl'

import AirPopulation from 'components/molecules/AirPopulation'
import { BabyPoopCard } from 'components/organisms/embededHeathToolCards/baby-poop'
import { BabyVaccinationCard } from 'components/organisms/embededHeathToolCards/baby-vaccination'
import { BabyGrowthCard } from 'components/organisms/embededHeathToolCards/embedBabyGrowth'
import { BMICardWrapper } from 'components/organisms/embededHeathToolCards/embedBMI'
import { BMRCardWrapper } from 'components/organisms/embededHeathToolCards/embedBMR'
import { DueDateCardWrapper } from 'components/organisms/embededHeathToolCards/embedDueDate'
import { OvulationCardWrapper } from 'components/organisms/embededHeathToolCards/embedOvulation'
import { PWGCardWrapper } from 'components/organisms/embededHeathToolCards/embedPwg'
import { THRCardWrapper } from 'components/organisms/embededHeathToolCards/embedTHR'
import { RiskScreenerLayout } from 'components/organisms/serviceConnections/riskScreenerLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import { SpotlightHealthToolSession } from 'interfaces/types'

import useStyles from './index.styles'

type Props = {
  data: SpotlightHealthToolSession
  applyH1?: boolean
} & BoxProps

export const HealthToolEmbed = ({
  data,
  styles,
  classNames,
  applyH1,
}: Props) => {
  const { classes } = useStyles(undefined, {
    name: 'HealthToolEmbed',
    styles,
    classNames,
  })
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()
  const template = data?.healthTool?.template || ''
  const embedStyles = (theme: MantineTheme) => ({
    root: {},
    bodyWrapper: {
      [theme.fn.largerThan('sm')]: {
        paddingTop: 32,
      },
    },
  })
  const content = (
    <Box
      sx={(theme) => ({
        borderRadius: 'inherit',
        ...(template === HEALTH_TOOL_NAME.BABY_POOP && {
          [theme.fn.largerThan('sm')]: {
            display: 'flex',
          },
        }),
      })}
    >
      {template === HEALTH_TOOL_NAME.BABY_POOP && (
        <Box
          sx={(theme) => ({
            flexShrink: 0,
            padding: '16px 16px 0 16px',
            [theme.fn.largerThan('sm')]: {
              padding: '32px 0 0 32px',
            },
          })}
        >
          <img
            alt="babyPoop"
            loading="lazy"
            src={CommonGAssets.getAssetPath('baby-poop-embed.png')}
            style={{ width: '120px' }}
          />
        </Box>
      )}
      {template === HEALTH_TOOL_NAME.BABY_GROWTH ? (
        <Box
          sx={(theme) => ({
            padding: rem(16),
            [theme.fn.largerThan('sm')]: {
              padding: rem(32),
              gap: rem(24),
            },
          })}
        >
          <BabyGrowthCard
            className="baby-growth-card"
            onlyUseForm
            styles={embedStyles}
          />
        </Box>
      ) : template === HEALTH_TOOL_NAME.BMI ? (
        <Box
          sx={(theme) => ({
            borderRadius: 'inherit',
            [theme.fn.largerThan('sm')]: {
              '.mantine-HealthToolFormWrapper-root': {
                paddingBottom: 0,
              },
            },
          })}
        >
          <BMICardWrapper
            onlyUseForm
            positionType="article"
            styles={embedStyles}
            className="bmi-card-wrapper"
          />
        </Box>
      ) : template === HEALTH_TOOL_NAME.BMR ? (
        <BMRCardWrapper
          onlyUseForm
          positionType="article"
          styles={embedStyles}
        />
      ) : template === HEALTH_TOOL_NAME.THR ? (
        <THRCardWrapper
          onlyUseForm
          positionType="article"
          styles={embedStyles}
        />
      ) : template === HEALTH_TOOL_NAME.BABY_POOP ? (
        <div className="baby-poop-card">
          <BabyPoopCard onlyUseForm styles={embedStyles} />
        </div>
      ) : template === HEALTH_TOOL_NAME.PWG ? (
        <PWGCardWrapper onlyUseForm styles={embedStyles} />
      ) : template === HEALTH_TOOL_NAME.OVULATION ? (
        <OvulationCardWrapper onlyUseForm styles={embedStyles} />
      ) : template === HEALTH_TOOL_NAME.BABY_VACCINE ? (
        <BabyVaccinationCard onlyUseForm styles={embedStyles} />
      ) : template === HEALTH_TOOL_NAME.DUE_DATE ? (
        <DueDateCardWrapper
          className="due-date-card"
          onlyUseForm
          styles={embedStyles}
        />
      ) : template === HEALTH_TOOL_NAME.AIR_POPULATION ? (
        <Box
          sx={(theme) => ({
            padding: 16,
            [theme.fn.largerThan('sm')]: {
              padding: 40,
            },
          })}
        >
          <AirPopulation />
        </Box>
      ) : null}
    </Box>
  )

  return (
    <RiskScreenerLayout
      applyH1={applyH1}
      title={data?.title}
      icon={data?.healthTool?.icon}
      reviewedBy={data?.healthTool?.reviewedBy}
      bodyRatio={'none'}
      disclaimer={{
        heading: f({ id: 'tools.disclaimer' }),
        description: data?.healthTool?.disclaimer || '',
      }}
      classNames={{
        boxFrame: classes.body,
        boxFrameInner: classes.bodyInner,
      }}
    >
      {isMobile ? (
        content
      ) : (
        <ScrollArea
          h="100%"
          w="100%"
          style={{
            borderRadius: 'inherit',
          }}
        >
          {content}
        </ScrollArea>
      )}
    </RiskScreenerLayout>
  )
}
